<template>
  <section
    v-if="products.length > 0"
    :class="bemBlockMod({ 'wrap-items': wrapItems })"
    :data-context="tileContext"
    :data-location="locationForAnalytics"
  >
    <p v-if="heading" :class="bemElement('heading')">
      {{ heading }}
    </p>
    <div
      ref="swiper"
      :class="[bemElement('slider'), 'swiper', 'u-scrollbar-none']"
      itemtype="http://schema.org/SomeProducts"
    >
      <div class="swiper-wrapper">
        <div
          v-for="(item, index) in products"
          :key="`product-slider-${index}`"
          class="swiper-slide"
          :class="bemElement('item')"
        >
          <BaseProductTile
            :analytics="buildAnalyticsPropForProduct(index)"
            :context="tileContext"
            :keyid="`${item.product.id}-${index}`"
            :grid-index="index"
            :has-carousel="isBreakpointDesktop"
            :hide-product-names="hideProductNames"
            :hide-prices="hidePrices"
            :hide-discount="hideDiscount"
            :hide-quick-shop="hideQuickShop"
            :hide-tags="hideTileTags"
            :show-swatches="false"
            :product="item.product"
            :url="item.urls.product"
            :is-recommendation-from-einstein="isRecommendationFromEinstein"
            :recommender-name="recommenderName"
          />
        </div>
      </div>
      <div v-if="showScrollbar" :class="bemElement('scrollbar')">
        <div
          ref="scrollbarProgress"
          :class="bemElement('scrollbar-progress')"
        />
      </div>
      <!-- Same side zones and arrows as the home custom slider, styled by its classes -->
      <template v-if="navigation">
        <div
          :class="[
            bemElement('scroll-hover'),
            { [bemElement('scroll-hover--click')]: navigation === 'click' },
            'the-home-custom-slider__scroll-hover',
            'the-home-custom-slider__scroll-hover--left',
            { 'the-home-custom-slider__scroll-hover--hidden': isBeginning },
          ]"
          @mouseenter="onNavigationEnter(-1)"
          @mouseleave="stopHoverScroll"
          @click="onNavigationClick(-1)"
        />
        <div
          :class="[
            bemElement('scroll-hover'),
            { [bemElement('scroll-hover--click')]: navigation === 'click' },
            'the-home-custom-slider__scroll-hover',
            'the-home-custom-slider__scroll-hover--right',
            { 'the-home-custom-slider__scroll-hover--hidden': isEnd },
          ]"
          @mouseenter="onNavigationEnter(1)"
          @mouseleave="stopHoverScroll"
          @click="onNavigationClick(1)"
        />
      </template>
    </div>
  </section>
</template>

<script>
import getFrontConfig from '../../common/getFrontConfig';
import bemClassesMixin from '../mixins/bemClassesMixin';
import windowResizeMixin from '../mixins/windowResizeMixin';
import BaseProductTile from './BaseProductTile.vue';
import EventBusConfig from '../config/eventBusConfig';
import getAnalyticsEventBus from '../../common/getAnalyticsEventBus';
import analyticsMixin from '../mixins/analyticsMixin';

const {
  responsive: { bp_1025: bp1025 },
  vue: {
    BaseProductTile: { context },
  },
} = getFrontConfig();

const analyticsEventBus = getAnalyticsEventBus();

// One pixel every 10ms: the default speed of the home custom slider hover
// zones (scrollHoverTime 1000 / scrollHoverDistance 100).
const HOVER_SCROLL_STEP_MS = 10;

export default {
  name: 'BaseProductSlider',

  components: {
    BaseProductTile,
  },

  mixins: [analyticsMixin, bemClassesMixin, windowResizeMixin],

  props: {
    heading: {
      type: String,
      default: null,
    },
    products: {
      type: Array,
      default() {
        return [];
      },
    },
    tileContext: {
      type: String,
      default: context.pdpSlider,
    },
    isRecommendationFromEinstein: {
      type: Boolean,
      default: false,
    },
    recommenderName: {
      type: String,
      default: '',
    },
    wrapItems: {
      type: Boolean,
      default: false,
    },
    hideProductNames: {
      type: Boolean,
      default: false,
    },
    hidePrices: {
      type: Boolean,
      default: false,
    },
    hideDiscount: {
      type: Boolean,
      default: false,
    },
    hideTags: {
      type: Boolean,
      default: false,
    },
    hideQuickShop: {
      type: Boolean,
      default: false,
    },
    showScrollbar: {
      type: Boolean,
      default: false,
    },
    /**
     * Side arrows, as in the home custom slider: 'hover' scrolls while the
     * pointer rests on them, 'click' moves one slide per click. Off when empty.
     */
    navigation: {
      type: String,
      default: null,
      validator: (value) => [null, 'hover', 'click'].includes(value),
    },
  },

  data: () => ({
    dataChangeBreakpoint: bp1025,
    hoverScrollTimer: null,
    isBeginning: true,
    isEnd: false,
    isSwiperImported: false,
    isSwiperInitialized: false,
    resizeObserver: null,
    swiperInstance: null,
  }),

  computed: {
    hideTileTags() {
      // The prop is what the product detail recommendations use, since their
      // tags became configurable: resolving them by context would reach the
      // look detail too, which has no preferences of its own.
      if (this.hideTags) {
        return true;
      }
      return [
        context.minicartSlider,
        context.minicartSliderEmptyCart,
        context.homeRecommendedEinsteinProducts,
        context.searchModal,
      ].includes(this.tileContext);
    },
    locationForAnalytics() {
      /**
       * A key/value object to transform the prop 'conext' of BaseProductTile to the
       * location property of the payload of a ProductTileClickedEvent event.
       * @type {Object.<jsdocTypes.BaseProductTileContext, string>}
       */
      const tileContextToLocation = {
        [context.homeRecommendedEinsteinProducts]:
          this.analyticsConst.location.homeRecommendedEinsteinProducts,
        [context.homeShopTheLook]: this.analyticsConst.location.homeShopTheLook,
        [context.minicartSlider]:
          this.analyticsConst.location.cartRecommendedProducts,
        [context.minicartSliderEmptyCart]:
          this.analyticsConst.location.emptyCartRecommendedProducts,
        [context.pdpRecommendedSlider]:
          this.analyticsConst.location.pdpRecommendedProducts,
        [context.pdpSlider]: this.analyticsConst.location.pdpCompleteTheLook,
        [context.searchModal]: this.analyticsConst.location.searchModal,
      };
      return tileContextToLocation[this.tileContext];
    },
    searchedTermForAnalytics() {
      let searchedTerm;
      if (this.tileContext === context.searchModal) {
        searchedTerm =
          analyticsEventBus.EVENTS.PAYLOADS.searchedTermNoText.type.featured;
      }
      return searchedTerm;
    },
  },

  watch: {
    products(newValue, oldValue) {
      if (newValue?.length !== oldValue?.length) {
        if (oldValue?.length > 0) {
          this.destroySwiper();
        }
        if (newValue?.length > 0) {
          this.initializeSwiper();
        }
        this.$eventBus.$emit(EventBusConfig.events.BaseProductSlider.updated, {
          products: this.products,
          productIds: this.products.map((item) => item.product.id),
          tileContext: this.tileContext,
        });
      }
    },
  },

  beforeCreate() {
    /*
    The component is prepared to load Swiper dinamically. However, it is is used
    whenever a carousel is shown in viewport. That condition is met when loading
    PLP, so will force to load the partial at the beginning.
    Although this partial seems useless, it will be cached by the browser and it
    will have less modifications than having code of the project.
    */
    import(/* webpackChunkName: "swiper.js" */ 'swiper').then(() => {
      this.isSwiperImported = true;
    });
  },

  beforeDestroy() {
    this.stopHoverScroll();
    this.destroySwiper();
    this.resizeObserver?.disconnect();
  },

  mounted() {
    this.initializeSwiper();
  },

  methods: {
    buildAnalyticsPropForProduct(position) {
      return this.analyticsExtendProp({
        location: this.locationForAnalytics,
        position,
        searchedTerm: this.searchedTermForAnalytics,
      });
    },
    destroySwiper() {
      this.stopHoverScroll();
      this.swiperInstance?.off('progress', this.updateEdges);
      this.swiperInstance?.destroy();
      this.isSwiperInitialized = false;
    },
    updateImageSliderHeight() {
      const imageSliderHeight =
        this.$refs.swiper.querySelector('.image-container')?.offsetHeight || 0;
      this.$refs.swiper.style.setProperty(
        '--image-slider-height',
        `${imageSliderHeight}px`
      );
    },
    setScrollbarProgress(progress) {
      this.$refs.scrollbarProgress.style.width = `${progress * 100}%`;
    },
    updateEdges() {
      this.isBeginning = this.swiperInstance.isBeginning;
      this.isEnd = this.swiperInstance.isEnd;
    },
    /**
     * Moves the slider one pixel, the way the home custom slider scrolls while
     * the pointer rests on one of its hover zones. Swiper moves the wrapper with
     * a transform, so scrollLeft is no use here: the translate is set directly,
     * within the slider bounds, and the run stops at the edge.
     * @param {number} direction 1 towards the end, -1 towards the beginning
     */
    hoverScrollStep(direction) {
      const swiper = this.swiperInstance;
      const translate = Math.min(
        swiper.minTranslate(),
        Math.max(swiper.maxTranslate(), swiper.translate - direction)
      );
      swiper.setTranslate(translate);
      swiper.updateActiveIndex();
      swiper.updateSlidesClasses();
      if (direction > 0 ? swiper.isEnd : swiper.isBeginning) {
        this.stopHoverScroll();
      }
    },
    startHoverScroll(direction) {
      if (!this.swiperInstance) return;
      this.stopHoverScroll();
      this.hoverScrollTimer = setInterval(
        () => this.hoverScrollStep(direction),
        HOVER_SCROLL_STEP_MS
      );
    },
    stopHoverScroll() {
      clearInterval(this.hoverScrollTimer);
      this.hoverScrollTimer = null;
    },
    onNavigationEnter(direction) {
      if (this.navigation === 'hover') this.startHoverScroll(direction);
    },
    onNavigationClick(direction) {
      if (this.navigation !== 'click' || !this.swiperInstance) return;
      if (direction > 0) {
        this.swiperInstance.slideNext();
      } else {
        this.swiperInstance.slidePrev();
      }
    },
    initializeSwiper() {
      if (this.isSwiperInitialized) return;
      if (this.products?.length <= 0) return;
      import(/* webpackChunkName: "swiper.js" */ 'swiper').then((module) => {
        const Swiper = module.default;
        const isTouchDisabled =
          this.wrapItems ||
          [context.minicartSliderEmptyCart, context.productSet].includes(
            this.tileContext
          ) ||
          (this.tileContext === context.minicartSlider &&
            this.isBreakpointDesktop);
        const swiperOptions = {
          loop: false,
          preventClicks: false,
          preventClicksPropagation: false,
          slidesPerView: 'auto',
          spaceBetween: 2,
          touchStartPreventDefault: false,
          allowTouchMove: !isTouchDisabled,
          simulateTouch: !isTouchDisabled,
        };
        if (this.showScrollbar) {
          swiperOptions.watchOverflow = true;
          swiperOptions.on = {
            progress: (swiper, progress) => {
              if (swiper.isLocked) return;
              this.setScrollbarProgress(progress);
            },
            lock: () => {
              this.setScrollbarProgress(1);
            },
            unlock: ({ progress }) => {
              this.setScrollbarProgress(progress);
            },
          };
        }
        this.swiperInstance = new Swiper(this.$refs.swiper, swiperOptions);
        if (this.showScrollbar && this.swiperInstance.isLocked) {
          this.setScrollbarProgress(1);
        }
        if (this.navigation) {
          // A locked slider (every item fits) is at both edges, so both zones hide.
          this.updateEdges();
          this.swiperInstance.on('progress', this.updateEdges);
        }
        this.isSwiperInitialized = true;
        this.$eventBus.$emit(EventBusConfig.events.BaseProductSlider.ready, {
          products: this.products,
          productIds: this.products.map((item) => item.product.id),
          tileContext: this.tileContext,
        });
        if (this.hideTileTags) {
          this.updateImageSliderHeight();
          if (!this.resizeObserver) {
            this.resizeObserver = new ResizeObserver(
              this.updateImageSliderHeight
            );
            this.resizeObserver.observe(this.$refs.swiper);
          }
        }
      });
    },
  },
};
</script>
